import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import EventNoteRoundedIcon from '@mui/icons-material/EventNoteRounded';
import FitnessCenterRoundedIcon from '@mui/icons-material/FitnessCenterRounded';
import MonitorHeartRoundedIcon from '@mui/icons-material/MonitorHeartRounded';
import RestaurantMenuRoundedIcon from '@mui/icons-material/RestaurantMenuRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import VideocamRoundedIcon from '@mui/icons-material/VideocamRounded';
import React, { ReactElement } from 'react';

/**
 * Patient (Client-role) navigation. Single source of truth — consumed by
 * AuthenticatedLayout (desktop sidebar + mobile drawer + bottom nav) and
 * ClientDashboard (mobile drawer fallback).
 *
 * Limited today to nav items whose destination route actually serves Clients.
 * Drop new entries in here as their routes ship — both surfaces pick them up.
 */
export interface PatientNavItem {
  value: string;
  label: string;
  icon: ReactElement;
}

export const PATIENT_SIDEBAR_TITLE = 'My Health';

export const patientNavItems: PatientNavItem[] = [
  { value: 'dashboard', label: 'Dashboard', icon: <DashboardRoundedIcon /> },
  { value: 'messages', label: 'Messages', icon: <ChatBubbleOutlineRoundedIcon /> },
  // Broadcasts intentionally absent: /broadcasts is practitioner-author UI
  // (gated by RoleGuard per HA-259). Clients see broadcasts via the
  // BroadcastList widget on their dashboard.
  // HA-266: links to /appointments (patient-facing standalone Appointments
  // page). Reuses /api/v1/telehealth/my-sessions; self-scheduling +
  // practitioner profile + reschedule/cancel ship with HA-273.
  { value: 'appointments', label: 'Appointments', icon: <EventNoteRoundedIcon /> },
  { value: 'telehealth', label: 'Telehealth', icon: <VideocamRoundedIcon /> },
  // HA-260: links to /my-forms (the patient-facing forms list). The
  // practitioner author UI lives at /forms and is gated by HA-259.
  { value: 'my-forms', label: 'Forms', icon: <AssignmentOutlinedIcon /> },
  // HA-261: links to /my-meal-plans (patient-facing assigned meal plans).
  // The practitioner author UI lives at /meal-plans and is gated by HA-259.
  { value: 'my-meal-plans', label: 'Meal Plans', icon: <RestaurantMenuRoundedIcon /> },
  // HA-262: links to /my-programs (patient-facing assigned training programs).
  // The practitioner author UI lives at /training and is gated by HA-259.
  { value: 'my-programs', label: 'Programs', icon: <FitnessCenterRoundedIcon /> },
  // HA-264: links to /health-metrics (patient-facing standalone Health Metrics
  // page). MVP shell today — real chart history + log CRUD ship with HA-272.
  { value: 'health-metrics', label: 'Health Metrics', icon: <MonitorHeartRoundedIcon /> },
  { value: 'settings', label: 'Settings', icon: <SettingsRoundedIcon /> },
];

export const patientBottomNavItems: PatientNavItem[] = [
  { value: 'dashboard', label: 'Home', icon: <DashboardRoundedIcon /> },
  { value: 'messages', label: 'Messages', icon: <ChatBubbleOutlineRoundedIcon /> },
  { value: 'settings', label: 'Settings', icon: <SettingsRoundedIcon /> },
];
